import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const RiderVerification = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [formValues, setFormValues] = useState({
    licenseNumber: '',
    vehicleInfo: {
      type: 'bike',
      model: '',
      year: '',
      color: '',
      plateNumber: '',
      seats: ''
    }
  });
  const [files, setFiles] = useState({
    licensePhoto: null,
    vehiclePhoto: null,
    idProof: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('vehicleInfo.')) {
      const key = name.replace('vehicleInfo.', '');
      setFormValues(v => ({ ...v, vehicleInfo: { ...v.vehicleInfo, [key]: value } }));
    } else {
      setFormValues(v => ({ ...v, [name]: value }));
    }
  };

  const handleFileChange = (e) => {
    const { name, files: fileList } = e.target;
    setFiles(f => ({ ...f, [name]: fileList && fileList[0] ? fileList[0] : null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!files.licensePhoto || !files.vehiclePhoto || !files.idProof) {
      toast.error('Please upload all required documents');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('licenseNumber', formValues.licenseNumber);
      formData.append('vehicleInfo.type', formValues.vehicleInfo.type);
      formData.append('vehicleInfo.model', formValues.vehicleInfo.model);
      formData.append('vehicleInfo.year', formValues.vehicleInfo.year);
      formData.append('vehicleInfo.color', formValues.vehicleInfo.color);
      formData.append('vehicleInfo.plateNumber', formValues.vehicleInfo.plateNumber);
      if (formValues.vehicleInfo.seats) {
        formData.append('vehicleInfo.seats', formValues.vehicleInfo.seats);
      }
      formData.append('licensePhoto', files.licensePhoto);
      formData.append('vehiclePhoto', files.vehiclePhoto);
      formData.append('idProof', files.idProof);

      const response = await api.post('/api/users/verify-rider', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      updateUser(response.data.user);
      toast.success('Verification submitted. Awaiting approval.');
      navigate('/profile');
    } catch (error) {
      const message = error.response?.data?.message || 'Submission failed';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || user.role !== 'rider') {
    return (
      <div className="max-w-2xl mx-auto">
        <p className="text-center text-gray-600">Only riders can submit verification.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Rider Verification</h1>
      <form onSubmit={handleSubmit} className="card space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">License Number</label>
          <input
            type="text"
            name="licenseNumber"
            className="input-field"
            value={formValues.licenseNumber}
            onChange={handleChange}
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Type</label>
            <select
              name="vehicleInfo.type"
              className="input-field"
              value={formValues.vehicleInfo.type}
              onChange={handleChange}
              required
            >
              <optgroup label="Two Wheelers">
                <option value="bike">Bike</option>
                <option value="scooter">Scooter</option>
              </optgroup>
              <optgroup label="Four Wheelers">
                <option value="car">Car</option>
                <option value="sedan">Sedan</option>
                <option value="hatchback">Hatchback</option>
                <option value="suv">SUV</option>
              </optgroup>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
            <input
              type="text"
              name="vehicleInfo.model"
              className="input-field"
              value={formValues.vehicleInfo.model}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
            <input
              type="number"
              name="vehicleInfo.year"
              className="input-field"
              value={formValues.vehicleInfo.year}
              onChange={handleChange}
              min="1990"
              max={new Date().getFullYear()}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
            <input
              type="text"
              name="vehicleInfo.color"
              className="input-field"
              value={formValues.vehicleInfo.color}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Plate Number</label>
            <input
              type="text"
              name="vehicleInfo.plateNumber"
              className="input-field"
              value={formValues.vehicleInfo.plateNumber}
              onChange={handleChange}
              required
            />
          </div>
          {['car', 'sedan', 'hatchback', 'suv'].includes(formValues.vehicleInfo.type) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of Seats (excluding driver)
              </label>
              <input
                type="number"
                name="vehicleInfo.seats"
                className="input-field"
                value={formValues.vehicleInfo.seats}
                onChange={handleChange}
                min="1"
                max="10"
                required={['car', 'sedan', 'hatchback', 'suv'].includes(formValues.vehicleInfo.type)}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">License Photo</label>
            <input type="file" name="licensePhoto" accept="image/*,application/pdf" onChange={handleFileChange} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Photo</label>
            <input type="file" name="vehiclePhoto" accept="image/*,application/pdf" onChange={handleFileChange} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">ID Proof</label>
            <input type="file" name="idProof" accept="image/*,application/pdf" onChange={handleFileChange} required />
          </div>
        </div>

        <button type="submit" disabled={submitting} className="btn-primary">
          {submitting ? 'Submitting...' : 'Submit for Verification'}
        </button>
      </form>
    </div>
  );
};

export default RiderVerification;


